import React, { useEffect, useMemo, useRef, useState } from "react";
import jsPDF from "jspdf";
import QRCode from "react-qr-code";
import { Badge, Button, Card, Col, Form, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import html2canvas from "html2canvas";
import "./style.css";

const MAX_RECENT_TAGS = 8;

// Encodes the table tag into the destination URL as a `table` query
// param, so the page it opens can read it and show which table an
// order/tab came from. Falls back gracefully if `url` isn't a fully
// qualified, parseable URL.
const buildQrValue = (baseUrl, tableTag) => {
  if (!baseUrl) return "";
  const trimmedTag = tableTag?.trim();
  if (!trimmedTag) return baseUrl;
  try {
    const parsed = new URL(baseUrl);
    parsed.searchParams.set("table", trimmedTag);
    return parsed.toString();
  } catch {
    const separator = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${separator}table=${encodeURIComponent(trimmedTag)}`;
  }
};

const slugify = (value) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const MenuQRCode = ({ url, spotName }) => {
  const [primaryColor, setPrimaryColor] = useState("#005092");
  const [secondaryColor, setSecondaryColor] = useState("#007cc4");
  const [footerColor, setFooterColor] = useState("#003a68");
  const [textColor, setTextColor] = useState("#ffffff");
  const [headerText, setHeaderText] = useState("See our menu");
  const [welcomeText, setWelcomeText] = useState("");
  const [tableTag, setTableTag] = useState("");
  const [recentTags, setRecentTags] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const cardRef = useRef();

  useEffect(() => {
    setWelcomeText(spotName ? `Welcome to ${spotName}` : "");
  }, [spotName]);

  const qrValue = useMemo(() => buildQrValue(url, tableTag), [url, tableTag]);

  const downloadPDF = async () => {
    if (!cardRef.current) return;
    setIsDownloading(true);
    try {
      // Render at a higher pixel density than the on-screen card so the
      // printed QR code stays crisp and scannable up close.
      const canvas = await html2canvas(cardRef.current, {
        scale: 3,
        backgroundColor: null
      });
      const imgData = canvas.toDataURL("image/png");

      // Fit the captured card onto an A4 page, centred, preserving its
      // aspect ratio. The previous version passed the canvas's raw pixel
      // dimensions straight in as millimetres, which produced a badly
      // mis-scaled (often cropped-looking) printout.
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const maxWidth = pageWidth - margin * 2;
      const maxHeight = pageHeight - margin * 2;
      const ratio = Math.min(
        maxWidth / canvas.width,
        maxHeight / canvas.height
      );
      const renderWidth = canvas.width * ratio;
      const renderHeight = canvas.height * ratio;
      const x = (pageWidth - renderWidth) / 2;
      const y = (pageHeight - renderHeight) / 2;

      pdf.addImage(imgData, "PNG", x, y, renderWidth, renderHeight);

      const tagSuffix = tableTag.trim() ? `_${slugify(tableTag)}` : "";
      pdf.save(`${spotName || "menu"}${tagSuffix}_qrcode.pdf`);

      if (tableTag.trim()) {
        setRecentTags((prev) => {
          const withoutDup = prev.filter((t) => t !== tableTag.trim());
          return [tableTag.trim(), ...withoutDup].slice(0, MAX_RECENT_TAGS);
        });
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="menu-qr-widget">
      <Card className="p-3 p-md-4 shadow-sm menu-qr-config-card mb-3">
        <h5 className="mb-3">Customize your QR card</h5>

        <Form.Group className="mb-3" controlId="formTableTag">
          <Form.Label className="fw-semibold">Table name / tag</Form.Label>
          <Form.Control
            type="text"
            value={tableTag}
            onChange={(e) => setTableTag(e.target.value)}
            placeholder="e.g. Table 5, Patio A3"
          />
          <Form.Text className="text-muted">
            Printed on the card and encoded into the QR link, so a scan can
            tell you which table an order came from.
          </Form.Text>
          {recentTags.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mt-2">
              {recentTags.map((tag) => (
                <Badge
                  key={tag}
                  bg="light"
                  text="dark"
                  className="menu-qr-tag-chip"
                  role="button"
                  tabIndex={0}
                  onClick={() => setTableTag(tag)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      setTableTag(tag);
                    }
                  }}
                >
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </Form.Group>

        <hr />

        {/* Deliberately xs-only columns: this widget always renders in a
            narrow, fixed-width host slot (a page sidebar). sm/md/lg
            column classes key off the BROWSER's viewport width, not this
            component's actual rendered width, so on a wide desktop
            screen they'd assume far more room than this slot actually
            has and overflow past it. xs has no such media query, so it
            lays out consistently regardless of where this is embedded. */}
        <Form.Group className="mb-3" controlId="formHeaderText">
          <Form.Label>Header text</Form.Label>
          <Form.Control
            type="text"
            value={headerText}
            onChange={(e) => setHeaderText(e.target.value)}
            placeholder="Enter header text"
          />
        </Form.Group>
        <Form.Group className="mb-3" controlId="formWelcomeText">
          <Form.Label>Welcome text</Form.Label>
          <Form.Control
            type="text"
            value={welcomeText}
            onChange={(e) => setWelcomeText(e.target.value)}
            placeholder="Enter welcome message"
          />
        </Form.Group>
        <Row className="g-3">
          <Col xs={6}>
            <Form.Group controlId="formPrimaryColor">
              <Form.Label className="d-block">Header</Form.Label>
              <Form.Control
                type="color"
                className="menu-qr-color-input"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col xs={6}>
            <Form.Group controlId="formSecondaryColor">
              <Form.Label className="d-block">Body</Form.Label>
              <Form.Control
                type="color"
                className="menu-qr-color-input"
                value={secondaryColor}
                onChange={(e) => setSecondaryColor(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col xs={6}>
            <Form.Group controlId="formFooterColor">
              <Form.Label className="d-block">Footer</Form.Label>
              <Form.Control
                type="color"
                className="menu-qr-color-input"
                value={footerColor}
                onChange={(e) => setFooterColor(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col xs={6}>
            <Form.Group controlId="formTextColor">
              <Form.Label className="d-block">Text</Form.Label>
              <Form.Control
                type="color"
                className="menu-qr-color-input"
                value={textColor}
                onChange={(e) => setTextColor(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
      </Card>

      <div
        id="qrCodeContainer"
        ref={cardRef}
        className="qrCodeContainer text-center p-4 mb-3 mx-auto"
        style={{
          "--primary-color": primaryColor,
          "--secondary-color": secondaryColor,
          "--gray-color": footerColor,
          "--text-color": textColor,
          color: textColor
        }}
      >
        <div className="upper-section mb-4">
          <h1>{headerText}</h1>
          {welcomeText && <p>{welcomeText}</p>}
        </div>

        <div className="middle-section mb-4">
          <QRCode
            id="qrCodeEl"
            value={qrValue}
            fgColor={primaryColor}
            className="qrCode"
            size={220}
            style={{ height: "auto", maxWidth: "70%", width: "70%" }}
          />
        </div>

        {tableTag.trim() && (
          <div className="table-tag-section mb-3">
            <Badge bg="light" text="dark" className="table-tag-badge">
              {tableTag.trim()}
            </Badge>
          </div>
        )}

        <div className="footer-section">
          <p className="mb-1">Scan me</p>
          <span>Powered by jamlyf</span>
        </div>
      </div>

      <Button
        variant="outline-primary"
        className="jamlyf-outline-button py-2 px-4 mx-auto d-flex align-items-center justify-content-center gap-2 menu-qr-download-btn"
        onClick={downloadPDF}
        disabled={!url || isDownloading}
      >
        <FontAwesomeIcon icon={faDownload} />
        {isDownloading ? "Preparing PDF…" : "Download as PDF"}
      </Button>
    </div>
  );
};

export default MenuQRCode;
